import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Send, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { usersAPI } from '../lib/api';
import { invitationsAPI, CLIENT_TYPE_OPTIONS } from '../lib/invitationsAPI';
import { servicesEngagementAPI } from '../lib/servicesEngagementAPI';
import { EngagementFeesEditor, initialEngagementFees, toFeePayload } from '../components/invitations/EngagementFeesEditor';
import { ENTITY_ROLES } from '../lib/signatoryRoles';

const NAMED_ENTITIES = ['company', 'trust', 'partnership', 'smsf'];

export default function NewClientInvitation() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const [fees, setFees] = useState(initialEngagementFees(['individual_tax_return']));
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', mobile: '',
    client_type: 'individual', entity_name: '', invitee_role: '',
    engagement_letter_template: 'auto',
    assigned_accountant_id: '', due_date: '', message: '',
  });
  const [accountants, setAccountants] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dup, setDup] = useState(null);
  const [created, setCreated] = useState(null); // { link } on success

  useEffect(() => {
    // Optional: populate assigned-accountant dropdown (admins only; ignore failures).
    usersAPI.list().then((res) => setAccountants(res.data.users || [])).catch(() => {});
    servicesEngagementAPI.listTemplates().then((res) => setTemplates(res.data.templates || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId) return;
    invitationsAPI.get(editId).then(({ data: inv }) => {
      const types = inv.engagement_types?.length ? inv.engagement_types : [inv.engagement_type].filter(Boolean);
      const base = initialEngagementFees(types);
      setFees({ ...base, types, fees: Object.fromEntries(types.map((t) => [t, { ...base.fees[t], ...((inv.fee_lines || []).find((x) => x.engagement_type === t) || {}) }])) });
      setForm({ first_name: inv.first_name || '', last_name: inv.last_name || '', email: inv.email || '', mobile: inv.mobile || '', client_type: inv.client_type || 'individual',
        entity_name: inv.entity_name || '', invitee_role: inv.invitee_role || '', engagement_letter_template: inv.engagement_letter_template || 'auto', assigned_accountant_id: inv.assigned_accountant_id || '',
        due_date: inv.due_date || '', message: inv.message || '' });
    }).catch(() => { toast.error('Invitation not found'); navigate('/new-clients'); });
  }, [editId, navigate]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const handleSubmit = async () => {
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error('First name, last name and email are required');
      return;
    }
    if (NAMED_ENTITIES.includes(form.client_type) && !form.entity_name.trim()) {
      toast.error('Entity / company name is required for this client type');
      return;
    }
    if (ENTITY_ROLES[form.client_type] && !ENTITY_ROLES[form.client_type].some(([k]) => k === form.invitee_role)) {
      toast.error('Select their role in the entity');
      return;
    }
    if (fees.types.length === 0) {
      toast.error('Select at least one engagement type');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        ...toFeePayload(fees),
        engagement_letter_template: form.engagement_letter_template === 'auto' ? null : form.engagement_letter_template,
        assigned_accountant_id: form.assigned_accountant_id || null,
        due_date: form.due_date || null,
        mobile: form.mobile || null,
        entity_name: NAMED_ENTITIES.includes(form.client_type) ? form.entity_name.trim() : null,
        invitee_role: ENTITY_ROLES[form.client_type] ? form.invitee_role : null,
        message: form.message || null,
        base_url: window.location.origin,
      };
      if (editId) {
        await invitationsAPI.update(editId, payload);
        toast.success('Invitation updated – use Resend to email the new details');
        navigate(`/new-clients/invite/${editId}`);
        return;
      }
      const res = await invitationsAPI.create(payload);
      setCreated({ link: res.data.link, name: (NAMED_ENTITIES.includes(form.client_type) && form.entity_name.trim()) || `${form.first_name} ${form.last_name}` });
      toast.success('Invitation sent');
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === 'PENDING_INVITE_EXISTS') {
        setDup(d);
        return;
      }
      toast.error(typeof d === 'string' ? d : (Array.isArray(d) ? d.map((x) => x.msg).join('; ') : 'Failed to send invitation'));
    } finally {
      setSubmitting(false);
    }
  };

  const resendExisting = async () => {
    try {
      await invitationsAPI.resend(dup.invitation_id, window.location.origin);
      toast.success('Existing invitation re-sent');
      setDup(null);
      navigate('/new-clients');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend the invitation');
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(created.link);
    toast.success('Link copied');
  };

  if (created) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <CardContent className="pt-10 pb-8 text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Invitation Sent
            </h2>
            <p className="text-muted-foreground mt-2">
              We emailed a secure registration link to <span className="font-medium">{created.name}</span>.
              You can also share the link directly:
            </p>
            <div className="mt-4 flex items-center gap-2 bg-muted/50 border rounded-lg p-3">
              <code className="text-xs text-foreground/90 break-all flex-1 text-left">{created.link}</code>
              <Button size="sm" variant="outline" onClick={copyLink}><Copy className="w-4 h-4" /></Button>
            </div>
            <div className="mt-6 flex justify-center gap-3">
              <Button variant="outline" onClick={() => { setCreated(null); setForm((f) => ({ ...f, first_name: '', last_name: '', email: '', mobile: '', message: '' })); }}>
                Send Another
              </Button>
              <Button onClick={() => navigate('/new-clients')}>View New Clients</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate(editId ? `/new-clients/invite/${editId}` : '/new-clients')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> New Clients
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {editId ? 'Edit Invitation' : 'New Client Invitation'}
          </CardTitle>
          <CardDescription>
            Send a prospective client a secure link to register and complete their onboarding.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First name *</Label>
              <Input value={form.first_name} onChange={onInput('first_name')} data-testid="inv-first-name" />
            </div>
            <div className="space-y-2">
              <Label>Last name *</Label>
              <Input value={form.last_name} onChange={onInput('last_name')} data-testid="inv-last-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={onInput('email')} data-testid="inv-email" />
            </div>
            <div className="space-y-2">
              <Label>Mobile (optional)</Label>
              <Input value={form.mobile} onChange={onInput('mobile')} data-testid="inv-mobile" />
            </div>
            <div className="space-y-2">
              <Label>Client type</Label>
              <Select value={form.client_type} onValueChange={set('client_type')}>
                <SelectTrigger data-testid="inv-client-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLIENT_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {NAMED_ENTITIES.includes(form.client_type) && (
              <div className="space-y-2">
                <Label>Entity / company name *</Label>
                <Input value={form.entity_name} onChange={onInput('entity_name')} placeholder="e.g. Smith Holdings Pty Ltd" data-testid="inv-entity-name" />
                <p className="text-xs text-muted-foreground">Used as the client's name. The first and last name above are the contact person.</p>
              </div>
            )}
            {ENTITY_ROLES[form.client_type] && (
              <div className="space-y-2">
                <Label>Their role *</Label>
                <Select value={form.invitee_role} onValueChange={set('invitee_role')}>
                  <SelectTrigger data-testid="inv-invitee-role"><SelectValue placeholder="Select their role" /></SelectTrigger>
                  <SelectContent>
                    {ENTITY_ROLES[form.client_type].map(([k, l]) => <SelectItem key={k} value={k} data-testid={`inv-role-${k}`}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">They become the entity's authorised signatory for engagement letters.</p>
              </div>
            )}
            <div className="space-y-2 md:col-span-2">
              <EngagementFeesEditor value={fees} onChange={setFees} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Engagement letter template</Label>
              <Select value={form.engagement_letter_template} onValueChange={set('engagement_letter_template')}>
                <SelectTrigger data-testid="inv-letter-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto — pick the best template for the client type</SelectItem>
                  {templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {form.engagement_letter_template === 'auto'
                  ? 'The engagement letter will default to the template matching the client type.'
                  : (templates.find((t) => t.key === form.engagement_letter_template) || {}).description}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Assigned accountant (optional)</Label>
              <Select value={form.assigned_accountant_id || 'none'} onValueChange={(v) => set('assigned_accountant_id')(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="inv-accountant"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {accountants.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name} ({a.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Onboarding due date (optional)</Label>
              <Input type="date" value={form.due_date} onChange={onInput('due_date')} data-testid="inv-due-date" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Message to client (optional)</Label>
            <Textarea rows={3} value={form.message} onChange={onInput('message')} placeholder="A short personal note included in the invitation email…" data-testid="inv-message" />
          </div>

          <div className="flex justify-end pt-2">
            {dup && (
              <div className="w-full rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex flex-wrap items-center gap-2" data-testid="inv-duplicate-warning">
                <span className="flex-1">{dup.message}</span>
                <Button size="sm" onClick={resendExisting} data-testid="inv-resend-existing">Resend existing invite</Button>
                <Button size="sm" variant="ghost" onClick={() => setDup(null)} data-testid="inv-duplicate-dismiss">Dismiss</Button>
              </div>
            )}
            <Button onClick={handleSubmit} disabled={submitting} data-testid="inv-submit">
              <Send className="w-4 h-4 mr-2" /> {submitting ? 'Saving…' : editId ? 'Save changes' : 'Send Invitation'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
